import {
    AdminPanelSettings,
    AutoAwesome,
    AutoGraph,
    CenterFocusStrong,
    Diversity3,
    EditNote,
    EmojiEvents,
    FavoriteBorder,
    FitnessCenter,
    Forum,
    Groups,
    HeartBroken,
    Psychology,
    Quiz,
    Schema,
    Shield,
    Speed,
    TrackChanges,
    Tune,
    VideoLibrary,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    FormControl,
    Grid,
    MenuItem,
    Select,
    Typography
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";
import { useSearchParams } from "react-router-dom";
import {
    DEFAULT_DOMAIN_LABELS,
    FLOW_STEPS,
    type FlowShot
} from "src/content/howItWorksBriefing";
import { Reveal } from "src/theme/animation";

const DEFAULT_IMAGES = [
    "/images/peer-peer.png",
    "/images/athlete-athlete.png",
    "/images/youth-guardian.png",
    "/images/ai-peer.png",
] as const;

interface Scenario {
    title: string;
    id: string;
    heroImage: string;
    description: string;
    individual: string;
    customizer: string;
    custom_domains?: string[];
}

const SCENARIOS: Scenario[] = [
    {
        title: "Youth with Parents / Guardians",
        id: "youth-guardian",
        heroImage: "/images/youth-guardian.jpeg",
        description: "Give high school youth and guardians tools to support each other as they answer their own questions to help them grow in confidence, communication, and leadership.",
        individual: "youth",
        customizer: "guardian",
        custom_domains: [
            "Self Belief",
            "Communication",
            "Emotional Regulation",
            "Resilience",
            "Leadership",
            "Concentration",
            "Mental Well-being",
            "Trust",
        ].sort(),
    },
    {
        title: "Athletes with Coaches",
        id: "club-athletes",
        heroImage: "/images/athlete-athlete.jpeg",
        description: "Give coaches and guardians tools to support club athletes as they answer their own questions to help them grow in composure, communication, and trust under pressure.",
        individual: "athlete",
        customizer: "coach",
        custom_domains: [
            "Performance Mindset",
            "Emotional Regulation",
            "Self Belief",
            "Resilience & Motivation",
            "Concentration",
            "Leadership",
            "Mental Well-being",
            "Communication",
        ].sort(),
    },
    {
        title: "Young Adults with Peers",
        id: "young-adults",
        heroImage: "/images/peer-peer.jpeg",
        description: "Give young adults tools to support each other as they answer their own questions to help them grow in confidence, communication, and leadership.",
        individual: "young adult",
        customizer: "peer",
        custom_domains: [
            "Self Belief",
            "Voice & Communication",
            "Leadership",
            "Emotional Regulation",
            "Resilience",
            "Concentration",
            "Mental Well-being",
            "Purpose",
        ].sort(),
    },
    {
        title: "Athletes Recovering from Injuries",
        id: "injured-athlete",
        heroImage: "/images/athlete-athlete.jpeg",
        description: "Give coaches and parents tools to support an injured athlete as they answer their own questions on the way back to sport — composure, self-belief, and readiness.",
        individual: "athlete",
        customizer: "coach",
        custom_domains: [
            "Recovery Readiness",
            "Self Belief",
            "Emotional Regulation",
            "Resilience & Motivation",
            "Concentration",
            "Communication",
            "Leadership",
            "Mental Well-being",
        ].sort(),
    },
    {
        title: "Young girls leading one another",
        id: "middle-school-girls",
        heroImage: "/images/peer-peer.jpeg",
        description: "Give middle-school girls tools to support each other as they answer their own questions to help them grow in voice, friendship, and leadership.",
        individual: "student",
        customizer: "peer",
        custom_domains: [
            "Self Belief",
            "Voice & Communication",
            "Leadership",
            "Emotional Regulation",
            "Friendship & Trust",
            "Concentration",
            "Resilience & Motivation",
            "Mental Well-being",
        ].sort(),
    },
    {
        title: "Employees with Managers and HR",
        id: "employees-managers",
        heroImage: "/images/peer-peer.jpeg",
        description: "Give managers and HR tools to support employees as they answer their own questions to help them grow in collaboration, communication, and leadership.",
        individual: "employee",
        customizer: "manager",
        custom_domains: [
            "Collaboration",
            "Leadership",
            "Communication",
            "Emotional Regulation",
            "Concentration",
            "Resilience & Motivation",
            "Self Belief",
            "Mental Well-being",
        ].sort(),
    },
    {
        title: "Clients with Clinicians and Caregivers",
        id: "clients-clinicians",
        heroImage: "/images/youth-guardian.jpeg",
        description: "Give clinicians and caregivers tools to support clients as they answer their own questions toward wellness, recovery, and resilience — a developmental overlay, not treatment.",
        individual: "client",
        customizer: "clinician",
        custom_domains: [
            "Resilience",
            "Wellness",
            "Self Belief",
            "Communication",
            "Emotional Regulation",
            "Concentration",
            "Leadership",
            "Motivation",
        ].sort(),
    }
];

const DEFAULT_DOMAIN_ICONS: Record<string, React.ElementType> = {
    "Performance Mindset": TrackChanges,
    "Emotional Regulation": HeartBroken,
    "Self Belief": EmojiEvents,
    "Resilience & Motivation": FitnessCenter,
    "Concentration": CenterFocusStrong,
    "Leadership": Groups,
    "Mental Well-being": Psychology,
    "Communication": Forum,
};

const HOW_IT_WORKS = [
    {
        title: "Guided Reflection",
        icon: <EditNote fontSize="small" />,
        color: "info.main",
        body: "Structured, step-by-step reflection helps each person name their own strengths and growth areas, with questions that build on their answers.",
    },
    {
        title: "Role-Based Visibility",
        icon: <AdminPanelSettings fontSize="small" />,
        color: "primary.main",
        body: "Participants, coaches, and guardians each see the right view: engagement trends and themes, never private reflections.",
    },
    {
        title: "Built-In Measurement",
        icon: <AutoGraph fontSize="small" />,
        color: "success.main",
        body: "Growth reports unlock automatically every 4 sessions, showing real change across 8 measurable domains.",
    },
] as const;

const AI_SUPERVISION = [
    {
        title: "Safety Moderation",
        icon: <Shield fontSize="small" />,
        color: "success.main",
        body: "AI automates alerts when documents or recordings include self-harm, hate speech, and other harmful content.",
    },
    {
        title: "Course Content",
        icon: <AutoAwesome fontSize="small" />,
        color: "info.main",
        body: "AI drafts the initial documents. The coach can revise with or without AI assistance before sharing with the individual or group.",
    },
    {
        title: "Media References",
        icon: <VideoLibrary fontSize="small" />,
        color: "warning.main",
        body: "AI can find relevant media content from approved sources or your own library.",
    },
] as const;

const EVIDENCE = [
    { value: "84%", color: "inherit", body: "of participants start their assessments" },
    { value: "72.3%", color: "success.light", body: "complete their lesson sessions" },
    { value: "66.4%", color: "warning.light", body: "submit post-program assessments" },
    { value: "≥ 6 of 8", color: "info.light", body: "domains improve ≥ +0.4, SMSP's own effectiveness benchmark" },
] as const;

const DIFFERENTIATORS = [
    {
        title: "Measurable",
        icon: <Speed />,
        color: "primary.main",
        body: "outcomes are tracked, not assumed, against a defined effectiveness benchmark.",
    },
    {
        title: "Strength-based",
        icon: <FavoriteBorder />,
        color: "success.main",
        body: "developmental and non-clinical. Growth is measured, never ranked or used for discipline.",
    },
    {
        title: "Relationship-centered",
        icon: <Diversity3 />,
        color: "info.main",
        body: "the mentor and mentee pairing is the engine, not a feature added to software.",
    },
    {
        title: "Scalable",
        icon: <Schema />,
        color: "warning.main",
        body: "the same loop works for 12 people or an entire organization, cohort after cohort.",
    },
] as const;

const isScreenshotsParamOn = (value: string | null, present: boolean): boolean => {
    if (!present) return false;
    const normalized = (value ?? "").trim().toLowerCase();
    return normalized !== "0" && normalized !== "false" && normalized !== "off";
};

const SECTION_REVEAL_SX = { mt: 3, mb: 2 } as const;

const SectionLabel: React.FC<{ children: React.ReactNode; invert?: boolean }> = ({ children, invert }) => (
    <Typography
        variant="overline"
        component="h2"
        className="how-it-works-section-label"
        sx={{
            fontWeight: 900,
            m: 0,
            p: 0,
            fontSize: "1.1rem",
            color: invert ? "inherit" : "text.secondary",
            display: "block",
            "@media print": {
                breakAfter: "avoid",
                pageBreakAfter: "avoid",
            },
        }}
    >
        {children}
    </Typography>
);

const ScreenshotStack: React.FC<{ shots: FlowShot[]; highlighted?: boolean }> = ({ shots, highlighted }) => {
    const [back, mid, front] = shots;
    const layers = [
        { shot: back, width: "64%", height: "72%", rotate: "6deg", top: 0, right: 0, z: 1, opacity: 0.9 },
        { shot: mid, width: "72%", height: "80%", rotate: "3deg", top: 10, right: 10, z: 2, opacity: 0.95 },
        { shot: front, width: "82%", height: "88%", rotate: "0deg", top: "auto", right: "auto", z: 3, opacity: 1 },
    ];

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: { xs: 200, md: 220 },
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "flex-start",
            }}
        >
            {layers.map(({ shot, width, height, rotate, top, right, z, opacity }) => (
                <Box
                    key={`${shot.src}-${shot.alt}`}
                    sx={{
                        position: z === 3 ? "relative" : "absolute",
                        top,
                        right,
                        zIndex: z,
                        width,
                        height,
                        borderRadius: 1,
                        overflow: "hidden",
                        opacity,
                        transform: `rotate(${rotate})`,
                        bgcolor: "background.paper",
                        border: 1,
                        borderColor: highlighted ? "success.light" : "divider",
                        boxShadow: z === 3 ? 4 : 1,
                    }}
                >
                    <Box
                        component="img"
                        src={shot.src}
                        alt={shot.alt}
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: shot.objectPosition,
                            display: "block",
                        }}
                    />
                </Box>
            ))}
        </Box>
    );
};

const HowItWorksPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [scenarioId, setScenarioId] = React.useState("");
    const scenario = SCENARIOS.find((item) => item.id === scenarioId) ?? null;
    const domainLabels = scenario?.custom_domains?.length
        ? scenario.custom_domains
        : [...DEFAULT_DOMAIN_LABELS];
    const showScreenshots = isScreenshotsParamOn(
        searchParams.get("screenshots"),
        searchParams.has("screenshots"),
    );

    const toggleScreenshots = () => {
        const next = new URLSearchParams(searchParams);
        if (showScreenshots) {
            next.delete("screenshots");
        } else {
            next.set("screenshots", "1");
        }
        setSearchParams(next, { replace: true });
    };

    return (
        <Container maxWidth="lg" >

            <Box data-label="Why It Matters" sx={SECTION_REVEAL_SX}>
                <Reveal variant="fadeUp">
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            borderLeft: 4,
                            borderLeftColor: "primary.main",
                        }}
                    >
                        <CardContent sx={{ p: 2 }}>
                            <SectionLabel>Why It Matters</SectionLabel>
                            <Typography variant="body1" sx={{ lineHeight: 1.7, color: "text.primary" }}>
                                Every organization that invests in young people wants them to grow in confidence, communication, and
                                leadership. SMSP gives that growth a shared language and a way to see it, across a mentor and mentee, a
                                team, a cohort, or a whole organization, along with a clear story to share with boards, funders, and
                                districts.
                            </Typography>
                        </CardContent>
                    </Card>
                </Reveal>
            </Box>

            <Box data-label="The Cascading Mentorship Model" sx={SECTION_REVEAL_SX}>
                <Reveal variant="fadeUp">
                    <Grid container justifyContent="space-between">
                        <Grid item xs={9}>
                            <SectionLabel>The Cascading Mentorship Model</SectionLabel>
                            <Typography variant="body1" color="text.secondary">
                                SMSP is a 12-session mentorship and reflection system. It pairs a mentor and mentee and gives every pair the
                                same structured loop:
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                variant={showScreenshots ? "contained" : "outlined"}
                                size="small"
                                color="primary"
                                onClick={toggleScreenshots}
                                data-attr="how-it-works_toggle-screenshots"
                            >
                                {showScreenshots ? "Hide screenshots" : "Show screenshots"}
                            </Button>
                        </Grid>
                    </Grid>
                </Reveal>

                <Reveal variant="fadeUp">
                    <Grid container justifyContent="space-between" spacing={2} mt={0} mb={1}>
                        {FLOW_STEPS.map((step, index) => (
                            <Grid item xs={4} sm={2.3} key={step.id}>
                                <Reveal variant="fadeUp" delay={index * 0.06}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: "100%",
                                            borderRadius: 2,
                                            ...(step.highlighted && {
                                                bgcolor: "primary.main",
                                                color: "primary.contrastText",
                                                borderColor: "primary.main",
                                            }),
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: showScreenshots ? 1.5 : 1,
                                                p: 2,
                                            }}
                                        >
                                            <Box sx={{ textAlign: "center" }}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: "block",
                                                        fontWeight: 800,
                                                        letterSpacing: 1.2,
                                                        textTransform: "uppercase",
                                                        color: step.highlighted ? "success.light" : `${step.accent}.main`,
                                                    }}
                                                >
                                                    {step.step}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                    {step.title}
                                                </Typography>
                                            </Box>
                                            {showScreenshots && (
                                                <ScreenshotStack shots={step.shots} highlighted={step.highlighted} />
                                            )}
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    textAlign: "center",
                                                    color: step.highlighted ? "primary.contrastText" : "text.secondary",
                                                    opacity: step.highlighted ? 0.85 : 1,
                                                }}
                                            >
                                                {step.caption}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Reveal>

                <Reveal variant="fadeUp">
                    <Typography variant="body1" color="text.secondary">
                        This loop repeats every cohort, so the
                        culture it builds does not depend on any one person staying in the room.
                    </Typography>
                </Reveal>
            </Box>

            <Box data-label="How It Works" className="avoid-break" sx={SECTION_REVEAL_SX}>
                <Reveal variant="fadeUp">
                    <SectionLabel>How It Works</SectionLabel>
                </Reveal>
                <Reveal variant="fadeUp">
                    <Grid container spacing={2}>
                        {HOW_IT_WORKS.map((item, index) => (
                            <Grid item xs={12} sm={4} key={item.title}>
                                <Reveal variant="fadeUp" delay={index * 0.08}>
                                    <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: item.color }}>
                                                {item.icon}
                                                <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                    {item.title}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                {item.body}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Reveal>
            </Box>

            <Box data-label="Always Relevant" className="avoid-break" sx={SECTION_REVEAL_SX}>
                <Reveal variant="fadeUp">
                    <Grid container justifyContent="space-between">
                        <Grid item xs={8}>
                            <SectionLabel>Always Relevant</SectionLabel>
                            <Typography variant="body1" color="text.secondary" >
                                Each 12-session course is written specifically for 1 individual.
                                3 inputs make that possible:
                            </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                            <FormControl size="small">
                                <Select
                                    size="small"
                                    value={scenarioId}
                                    variant="outlined"
                                    color="primary"
                                    displayEmpty
                                    onChange={(event) => setScenarioId(event.target.value)}
                                    data-attr="how-it-works_select-scenario"
                                    inputProps={{ "aria-label": "Use case" }}
                                >
                                    <MenuItem value="">common use cases...</MenuItem>
                                    {SCENARIOS.map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {item.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Reveal>
                <Reveal variant="fadeUp">
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12}>
                            <Reveal variant="fadeUp">
                                <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1, color: "info.main" }}>
                                            <Quiz fontSize="small" />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                The Individual's Assessment
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 1.5 }}>
                                            Use our Confidence Assessment, or write your own.
                                        </Typography>
                                        <Box
                                            sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                                            data-attr="how-it-works_domain-list"
                                        >
                                            {domainLabels.map((label) => {
                                                const Icon = DEFAULT_DOMAIN_ICONS[label];
                                                return (
                                                    <Chip
                                                        key={label}
                                                        label={label}
                                                        size="small"
                                                        icon={Icon ? <Icon /> : undefined}
                                                    />
                                                );
                                            })}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Reveal variant="fadeUp" delay={0.06}>
                                <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: "success.main" }}>
                                            <Groups fontSize="small" />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                Cohort / Community Context
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                            Who is this community or organization meant to support?
                                            Define the context so slides stay relevant for your team, club, workplace, or any other space.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Reveal>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Reveal variant="fadeUp" delay={0.12}>
                                <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: "warning.main" }}>
                                            <Tune fontSize="small" />
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                Coach Customizations
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                            Want to give individual guidance or context?
                                            Edit any document or media reference with or without AI to customize any part of the program.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Reveal>
                        </Grid>
                    </Grid>
                </Reveal>
            </Box>

            <Box data-label="How AI is used & supervised" className="avoid-break" sx={SECTION_REVEAL_SX}>
                <Reveal variant="fadeUp">
                    <SectionLabel>How AI is used &amp; supervised</SectionLabel>
                </Reveal>
                <Reveal variant="fadeUp">
                    <Grid container spacing={2}>
                        {AI_SUPERVISION.map((item, index) => (
                            <Grid item xs={12} sm={4} key={item.title}>
                                <Reveal variant="fadeUp" delay={index * 0.08}>
                                    <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                                        <CardContent sx={{ p: 2 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, color: item.color }}>
                                                {item.icon}
                                                <Typography variant="h6" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                    {item.title}
                                                </Typography>
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                                                {item.body}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Reveal>
            </Box>

            <div style={{ pageBreakAfter: "always" }}></div>
            <Box data-label="What the Evidence Shows" className="avoid-break" sx={SECTION_REVEAL_SX}>
                <Reveal variant="fadeUp">
                    <Box
                        sx={(theme) => ({
                            p: 2,
                            borderRadius: 2,
                            color: "primary.contrastText",
                            backgroundColor: theme.palette.primary.main,
                            backgroundImage: `radial-gradient(circle at 25% 25%, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.light} 100%)`,
                        })}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.light" }} />
                                <SectionLabel invert>What the Evidence Shows</SectionLabel>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: 0.85 }}>
                                SMSP Cohort Benchmark
                            </Typography>
                        </Box>
                        <Grid container spacing={2}>
                            {EVIDENCE.map((stat) => (
                                <Grid item xs={12} sm={6} md={3} key={stat.value}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            height: "100%",
                                            borderRadius: 1,
                                            bgcolor: (theme) => alpha(theme.palette.common.white, 0.1),
                                            border: "1px solid",
                                            borderColor: (theme) => alpha(theme.palette.common.white, 0.12),
                                        }}
                                    >
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 800,
                                                lineHeight: 1,
                                                color: stat.color === "inherit" ? "primary.contrastText" : stat.color,
                                            }}
                                        >
                                            {stat.value}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1, lineHeight: 1.5, opacity: 0.9 }}>
                                            {stat.body}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Reveal>
            </Box>

            <Box data-label="Why It's Different" className="avoid-break" sx={SECTION_REVEAL_SX}>
                <Reveal variant="fadeUp">
                    <SectionLabel>Why It’s Different</SectionLabel>
                </Reveal>
                <Reveal variant="fadeUp" className="avoid-break">
                    <Grid container spacing={2}>
                        {DIFFERENTIATORS.map((item, index) => (
                            <Grid item xs={12} sm={6} key={item.title}>
                                <Reveal variant="fadeUp" delay={index * 0.06}>
                                    <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                                        <CardContent sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 2 }}>
                                            <Box sx={{ color: item.color, mt: 0.25 }}>{item.icon}</Box>
                                            <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                                <Box component="strong" sx={{ fontWeight: 800, color: "text.primary" }}>
                                                    {item.title}:
                                                </Box>{" "}
                                                <Box component="span" sx={{ color: "text.secondary" }}>
                                                    {item.body}
                                                </Box>
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Reveal>
                            </Grid>
                        ))}
                    </Grid>
                </Reveal>
            </Box>
        </Container>
    );
};

export default HowItWorksPage;
